import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useTeamStore } from "../state/teamStore";
import { useRunStore } from "../state/runStore";
import { api } from "../utils/api";
import { formatLapTime, formatSetupName as setupFilename } from "../utils/formatters";
import TireVisual from "../components/TireVisual";
import type { RunSummary, RunDetail, TireData, WeatherData } from "../types";

function totalGreenTime(run: RunSummary): number | null {
  if (run.avgLapTime == null || run.greenLaps == null || run.greenLaps === 0) return null;
  return run.avgLapTime * run.greenLaps;
}

function fmtTotal(seconds: number | null): string {
  if (seconds == null) return "—";
  return `${seconds.toFixed(1)}s`;
}

function heatColor(val: number | null, lo: number, hi: number): string | undefined {
  if (val == null || !isFinite(lo) || !isFinite(hi) || lo === hi) return undefined;
  const ratio = (val - lo) / (hi - lo);
  if (ratio <= 0) return "var(--live)";
  if (ratio < 0.33) return "var(--live)";
  if (ratio < 0.66) return "var(--flag-orange)";
  return "var(--flag-red)";
}

const SESSION_TYPES = ["Test", "Practice", "Qualifying", "Race"] as const;
type SessionType = (typeof SESSION_TYPES)[number];

const SESSION_COLORS: Record<SessionType, string> = {
  Test: "var(--flag-purple)",
  Practice: "var(--live)",
  Qualifying: "var(--flag-orange)",
  Race: "var(--flag-red)",
};

export default function DriversBoard() {
  const { teamId, trackWeekId } = useParams<{ teamId?: string; trackWeekId?: string }>();
  const { activeTeamId } = useTeamStore();
  const { runs, selectedTrackWeekId, fetchRuns, error, loading: runsLoading } = useRunStore();
  const effectiveTeamId = teamId || activeTeamId;
  const effectiveTrackWeekId = trackWeekId || selectedTrackWeekId;

  const [runDetails, setRunDetails] = useState<Record<string, RunDetail>>({});
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<SessionType>>(new Set(["Test", "Practice", "Qualifying"]));

  const toggleFilter = (type: SessionType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type); // Don't allow empty selection
      } else {
        next.add(type);
      }
      return next;
    });
  };

  useEffect(() => {
    if (effectiveTeamId && effectiveTrackWeekId) fetchRuns(effectiveTeamId, effectiveTrackWeekId);
  }, [effectiveTeamId, effectiveTrackWeekId, fetchRuns]);

  useEffect(() => {
    if (runs.length === 0) return;
    const missing = runs.filter((r) => !runDetails[r.id]);
    if (missing.length === 0) return;
    let cancelled = false;
    setLoading(true);
    Promise.all(missing.map((r) => api<RunDetail>(`/api/runs/${r.id}`).then((d) => [r.id, d] as const).catch(() => null)))
      .then((results) => {
        if (cancelled) return; // a slower prior week's details must not land after navigation
        // Functional update: merging into a captured {...runDetails} would drop
        // results when two fetch waves overlap (fast week switch).
        setRunDetails((prev) => {
          const next = { ...prev };
          for (const r of results) if (r) next[r[0]] = r[1];
          return next;
        });
        setLoading(false);
      });
    return () => { cancelled = true; setLoading(false); };
  }, [runs]);

  // Filter runs by active session types
  const filteredRuns = useMemo(
    () => runs.filter((r) => activeFilters.has((r.eventType || "Test") as SessionType)),
    [runs, activeFilters]
  );

  const drivers = useMemo(() => {
    const map = new Map<string, { userId: string; displayName: string; runs: RunSummary[] }>();
    for (const r of filteredRuns) {
      if (!map.has(r.userId)) map.set(r.userId, { userId: r.userId, displayName: r.displayName || "Unknown", runs: [] });
      map.get(r.userId)!.runs.push(r);
    }
    return Array.from(map.values()).map((d) => ({
      ...d,
      runs: [...d.runs].sort((a, b) => (a.avgLapTime ?? Infinity) - (b.avgLapTime ?? Infinity)),
    }));
  }, [filteredRuns]);

  if (runs.length === 0) {
    if (runsLoading) {
      return (
        <div className="empty-state">
          <div className="spinner spinner-lg" />
          <div className="empty-state-title">Loading runs…</div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="empty-state">
          <div className="empty-state-title">Couldn't load runs</div>
          <div className="empty-state-text">{error}</div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: "0.75rem" }}
            onClick={() => effectiveTeamId && effectiveTrackWeekId && fetchRuns(effectiveTeamId, effectiveTrackWeekId)}
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <div className="empty-state">
        <div className="empty-state-title">No runs this week</div>
        <div className="empty-state-text">Upload .ibt files to start comparing setups.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      {/* A failed refresh keeps the prior runs on screen — say so rather than pass
          stale data off as current. */}
      {error && (
        <div
          style={{
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem",
            padding: "0.5rem 0.85rem", fontSize: "0.8125rem", color: "var(--flag-red)",
            border: "1px solid var(--flag-red)", borderRadius: 6,
          }}
        >
          <span>{error} Showing the last loaded runs.</span>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => effectiveTeamId && effectiveTrackWeekId && fetchRuns(effectiveTeamId, effectiveTrackWeekId)}
          >
            Retry
          </button>
        </div>
      )}
      {/* Tab content under the WeekWorkspace hero — no page title; filters only */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          {SESSION_TYPES.map((type) => {
            const active = activeFilters.has(type);
            return (
              <button
                key={type}
                onClick={() => toggleFilter(type)}
                style={{
                  padding: "0.3rem 0.6rem",
                  borderRadius: 6,
                  border: `1px solid ${active ? `color-mix(in srgb, ${SESSION_COLORS[type]} 38%, transparent)` : "var(--rule-faint)"}`,
                  background: active ? `color-mix(in srgb, ${SESSION_COLORS[type]} 9%, transparent)` : "transparent",
                  color: active ? SESSION_COLORS[type] : "var(--text-muted)",
                  fontFamily: "var(--font-body)",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {type}
              </button>
            );
          })}
          <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginLeft: "0.5rem" }}>
            {filteredRuns.length} runs{loading ? " · Loading" : ""}
          </span>
        </div>
      </div>

      {drivers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No runs match the filters</div>
          <div className="empty-state-text">Re-enable a session type above.</div>
        </div>
      ) : (
        drivers.map((driver) => (
          <DriverSheet key={driver.userId} driver={driver} details={runDetails} allRuns={filteredRuns} />
        ))
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DriverSheet — Spreadsheet columns = setups, clean rows
   ═══════════════════════════════════════════════════════════════════════════ */

function DriverSheet({ driver, details, allRuns }: {
  driver: { userId: string; displayName: string; runs: RunSummary[] };
  details: Record<string, RunDetail>;
  allRuns: RunSummary[];
}) {
  // Hooks must run before any early return (rules of hooks) — a zero-run driver
  // reaching the guard below would otherwise throw "rendered fewer hooks".
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState("");

  const R = driver.runs;
  if (!R.length) return null;

  // Global bests across ALL drivers for color coding
  const allBest = Math.min(...allRuns.map((r) => r.bestLapTime ?? Infinity));
  const allWorst = Math.max(...allRuns.filter((r) => r.bestLapTime != null).map((r) => r.bestLapTime!));

  const maxLaps = Math.max(0, ...R.map((r) => details[r.id]?.laps?.length ?? 0));

  const saveComment = async (runId: string) => {
    try {
      await api(`/api/runs/${runId}`, {
        method: "PUT",
        body: JSON.stringify({ comments: commentDraft }),
      });
      setEditingComment(null);
    } catch { /* ignore */ }
  };

  return (
    <section style={{ border: "1px solid var(--rule-faint)", borderRadius: 8, background: "var(--ink-3)", overflow: "hidden" }}>
      {/* Driver header — clickable to view driver detail */}
      <div style={{ padding: "0.6rem 0.85rem", borderBottom: "1px solid var(--rule-faint)", background: "var(--rule-faint)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Link to={`/runs/${R[0]?.id || ""}`} style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem", fontWeight: 700, color: "var(--text-strong)", textDecoration: "none" }}>
            {driver.displayName}
          </Link>
          <span style={{ marginLeft: 8, fontSize: "0.8125rem", color: "var(--text-muted)" }}>{R.length} setups</span>
        </div>
        <Link to={`/runs/${R[0]?.id || ""}`} style={{ fontSize: "0.8125rem", color: "var(--live)", textDecoration: "none" }}>
          View details →
        </Link>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {R.map((r, i) => {
                const et = (r.eventType || "Test") as SessionType;
                return (
                  <th
                    key={r.id}
                    colSpan={i === 0 ? 2 : 1}
                    style={{
                      ...base,
                      whiteSpace: "normal",
                      textAlign: "left",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "var(--text-muted)",
                      background: "var(--rule-faint)",
                      borderBottom: "1px solid var(--rule-faint)",
                      minWidth: i === 0 ? 250 : 160,
                    }}
                  >
                    <Link to={`/runs/${r.id}`} style={{ color: "var(--text-strong)", textDecoration: "none" }} title={r.setupName}>
                      <div style={{ fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.3, wordBreak: "break-word" }}>
                        {setupFilename(r.setupName)}
                      </div>
                    </Link>
                    <span style={{
                      display: "inline-block",
                      marginTop: "0.25rem",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      padding: "1px 6px",
                      borderRadius: 3,
                      color: SESSION_COLORS[et] || "var(--text-muted)",
                      background: `color-mix(in srgb, ${SESSION_COLORS[et] || "var(--text-muted)"} 9%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${SESSION_COLORS[et] || "var(--text-muted)"} 19%, transparent)`,
                    }}>
                      {et}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Best Lap — color coded */}
            <tr>
              <Label>Best Lap</Label>
              {R.map((r) => {
                const v = r.bestLapTime;
                const c = heatColor(v, allBest, allWorst);
                return (
                  <Cell key={r.id} style={{ fontVariantNumeric: "tabular-nums", fontWeight: v === allBest ? 700 : 400, color: c || "var(--text-strong)", background: c ? `color-mix(in srgb, ${c} 8%, transparent)` : undefined }}>
                    {formatLapTime(v)}
                  </Cell>
                );
              })}
            </tr>

            {/* Total Time (avg * green laps) */}
            <tr>
              <Label>Total Time</Label>
              {R.map((r) => (
                <Cell key={r.id} style={{ fontVariantNumeric: "tabular-nums" }}>{fmtTotal(totalGreenTime(r))}</Cell>
              ))}
            </tr>

            {/* Green Laps */}
            <tr>
              <Label>Green Laps</Label>
              {R.map((r) => <Cell key={r.id}>{r.greenLaps ?? 0}</Cell>)}
            </tr>

            {/* Weather — hover popup */}
            <tr>
              <Label>Weather</Label>
              {R.map((r) => (
                <Cell key={r.id}>
                  <WeatherPopup weather={r.weather} />
                </Cell>
              ))}
            </tr>

            {/* Tire Wear — center wear per corner, hover for full detail */}
            <tr>
              <Label>Tire Wear</Label>
              {R.map((r) => (
                <Cell key={r.id}>
                  <TirePopup tireData={details[r.id]?.tireData ?? null} unit={r.weather?.units} />
                </Cell>
              ))}
            </tr>

            {/* Comments — editable */}
            <tr>
              <Label>Comments</Label>
              {R.map((r) => (
                <Cell key={r.id} style={{ padding: "0.3rem 0.4rem" }}>
                  {editingComment === r.id ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <textarea
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        rows={3}
                        style={{ width: "100%", fontSize: "0.8125rem", background: "var(--ink-0)", border: "1px solid var(--rule-faint)", borderRadius: 4, color: "var(--text-strong)", padding: "0.3rem", resize: "vertical", fontFamily: "var(--font-body)" }}
                        autoFocus
                      />
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => saveComment(r.id)} style={{ fontSize: "0.8125rem", padding: "2px 8px", background: "var(--live)", color: "var(--text-inverse)", border: "none", borderRadius: 3, cursor: "pointer" }}>Save</button>
                        <button onClick={() => setEditingComment(null)} style={{ fontSize: "0.8125rem", padding: "2px 8px", background: "transparent", color: "var(--text-muted)", border: "1px solid var(--rule-faint)", borderRadius: 3, cursor: "pointer" }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => { setEditingComment(r.id); setCommentDraft(r.comments || ""); }}
                      style={{ cursor: "pointer", fontSize: "0.8125rem", color: r.comments ? "var(--text-secondary)" : "var(--text-muted)", lineHeight: 1.4, whiteSpace: "normal", maxWidth: 150, minHeight: 20 }}
                      title="Click to edit"
                    >
                      {r.comments || "Add a note"}
                    </div>
                  )}
                </Cell>
              ))}
            </tr>

            {/* Divider */}
            {maxLaps > 0 && <tr><td colSpan={R.length + 1} style={{ height: 2, background: "var(--rule-faint)", padding: 0 }} /></tr>}

            {/* Lap times */}
            {maxLaps > 0 && Array.from({ length: maxLaps }, (_, i) => {
              const times = R.map((r) => details[r.id]?.laps?.[i]?.lapTime ?? null);
              const valid = times.filter((t): t is number => t != null && t > 0);
              const lapBest = valid.length ? Math.min(...valid) : null;

              return (
                <tr key={`lap-${i}`}>
                  <Label>Lap {i + 1}</Label>
                  {R.map((r) => {
                    const lap = details[r.id]?.laps?.[i];
                    const t = lap?.lapTime ?? null;
                    const pit = lap?.pitIn || lap?.pitOut;
                    const inv = lap && !lap.isValid;
                    return (
                      <Cell key={r.id} style={{
                        fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem",
                        color: inv ? "var(--text-muted)" : pit ? "var(--live)" : t === lapBest ? "var(--live)" : undefined,
                        fontWeight: t === lapBest ? 600 : 400,
                        background: pit ? "var(--live-soft)" : undefined,
                      }}>
                        {t != null ? formatLapTime(t) : ""}{pit ? " P" : ""}
                      </Cell>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Hover Popups
   ═══════════════════════════════════════════════════════════════════════════ */

function WeatherPopup({ weather }: { weather: WeatherData | null }) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  if (!weather) return <span style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>—</span>;

  const skyIcons: Record<string, string> = { Clear: "☀", "Partly Cloudy": "⛅", "Mostly Cloudy": "☁", Overcast: "☁", Rain: "🌧" };
  const icon = weather.skies ? skyIcons[weather.skies] || "" : "";

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <div style={{ cursor: "pointer", fontSize: "0.8125rem", fontVariantNumeric: "tabular-nums", lineHeight: 1.4, textAlign: "center" }}>
        <div>{icon} {weather.airTemp != null ? `${Math.round(weather.airTemp)}°F` : "—"}</div>
        {weather.trackTemp != null && <div style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>Trk {Math.round(weather.trackTemp)}°</div>}
        {weather.humidity != null && <div style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>{Math.round(weather.humidity)}% hum</div>}
      </div>
      {show && (
        <div style={{
          position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          zIndex: 50, background: "var(--ink-3)", border: "1px solid var(--rule-faint)",
          borderRadius: 6, padding: "0.6rem 0.8rem", minWidth: 160, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}>
          <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>Weather</div>
          <div style={{ fontSize: "0.8125rem", display: "flex", flexDirection: "column", gap: 2 }}>
            <Row2 l="Air" v={weather.airTemp != null ? `${weather.airTemp}°F` : "—"} />
            <Row2 l="Track" v={weather.trackTemp != null ? `${weather.trackTemp}°F` : "—"} />
            <Row2 l="Humidity" v={weather.humidity != null ? `${weather.humidity}%` : "—"} />
            <Row2 l="Wind" v={weather.windVel != null ? `${weather.windVel} mph` : "—"} />
            <Row2 l="Skies" v={weather.skies ?? "—"} />
          </div>
        </div>
      )}
    </div>
  );
}

function TirePopup({ tireData, unit }: { tireData: TireData | null; unit?: string }) {
  const [show, setShow] = useState(false);

  if (!tireData || tireData.confidence === "none") return <span style={{ color: "var(--text-muted)", fontSize: "0.8125rem" }}>—</span>;

  // Center tire wear (M) for each corner
  const lf = tireData.LF?.wear?.M ?? null;
  const rf = tireData.RF?.wear?.M ?? null;
  const lr = tireData.LR?.wear?.M ?? null;
  const rr = tireData.RR?.wear?.M ?? null;
  const fmt = (v: number | null) => v != null ? `${Math.round(v)}%` : "—";
  const wc = (v: number | null) => {
    if (v == null) return "var(--text-muted)";
    if (v >= 90) return "var(--live)";
    if (v >= 70) return "var(--live)";
    if (v >= 50) return "var(--flag-orange)";
    return "var(--flag-red)";
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {/* Mini car layout: LF RF top, LR RR bottom — center wear only */}
      <div style={{ cursor: "pointer", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px 6px", fontVariantNumeric: "tabular-nums", fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.3 }}>
        <span style={{ color: wc(lf), textAlign: "right" }}>{fmt(lf)}</span>
        <span style={{ color: wc(rf), textAlign: "left" }}>{fmt(rf)}</span>
        <span style={{ color: wc(lr), textAlign: "right" }}>{fmt(lr)}</span>
        <span style={{ color: wc(rr), textAlign: "left" }}>{fmt(rr)}</span>
      </div>
      {show && (
        <div style={{
          position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
          zIndex: 50, background: "var(--ink-3)", border: "1px solid var(--rule-faint)",
          borderRadius: 8, padding: "0.75rem", minWidth: 240, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        }}>
          <TireVisual tireData={tireData} unit={unit} />
        </div>
      )}
    </div>
  );
}

function Row2({ l, v }: { l: string; v: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span style={{ color: "var(--text-muted)" }}>{l}</span>
      <span style={{ color: "var(--text-strong)", fontVariantNumeric: "tabular-nums" }}>{v}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Table primitives
   ═══════════════════════════════════════════════════════════════════════════ */

const base: React.CSSProperties = {
  padding: "0.3rem 0.5rem", textAlign: "center", fontSize: "0.8125rem",
  borderBottom: "1px solid var(--rule-faint)", whiteSpace: "nowrap",
};

function Label({ children }: { children: React.ReactNode }) {
  return <td style={{ ...base, textAlign: "left", fontWeight: 600, fontSize: "0.8125rem", color: "var(--text-muted)", background: "var(--ink-3)", paddingLeft: "0.75rem", minWidth: 90 }}>{children}</td>;
}

function Cell({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ ...base, ...style }}>{children}</td>;
}
